import { useEffect } from "react";

/**
 * Listens for barcode-scanner keyboard input on the document and invokes
 * ``onScan`` when a complete code is captured.
 *
 * Heuristic: a barcode scanner types many characters within a short window
 * and ends with Enter. We buffer keydown events; characters appended within
 * 50ms of the previous one count toward the same scan. Enter flushes the
 * buffer if at least 4 characters were accumulated.
 *
 * The hook installs no input element; the listener runs on ``document``
 * so it fires regardless of focus state. Disable while a request is
 * in-flight to avoid double-submits.
 */
export function useBarcodeScanner(
  onScan: (barcode: string) => void,
  enabled: boolean = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    let buffer = "";
    let lastEventAt = 0;
    const SCAN_GAP_MS = 60;
    const MIN_LEN = 4;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTypingInForm =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      const now = performance.now();
      // A fast BURST (this key arrived within the scan gap of the previous one)
      // is a scanner, not a human. We swallow burst keys so a scanned character
      // can never reach the browser's default handlers — in fullscreen, a stray
      // key from the scan otherwise drops fullscreen, and a scanned char could
      // trigger a page shortcut. Ordinary single keystrokes (gaps > the scan
      // window) and anything typed into a form input are left untouched.
      const inBurst = now - lastEventAt <= SCAN_GAP_MS;
      if (!inBurst) buffer = "";
      lastEventAt = now;

      if (e.key === "Enter") {
        if (buffer.length >= MIN_LEN && !isTypingInForm) {
          e.preventDefault(); // swallow the scanner's terminating Enter
          const captured = buffer;
          buffer = "";
          onScan(captured);
        }
        return;
      }
      // Append ANY single printable char (not just alphanumerics), so a
      // scanner's DELIMITER characters survive into the captured code. A voucher
      // code is YYYY-PREFIX-NNNNNN, but a QWERTZ tablet types the "-" as "ß"
      // (or "/", etc.); the voucher matcher is delimiter-agnostic yet still needs
      // the delimiter to be PRESENT. Dropping non-alphanumerics here collapsed
      // "2026-PALAP-123456" to "2026PALAP123456" and the match failed — so a
      // scanned voucher was never detected (manual entry kept the delimiter and
      // worked). Whitespace is excluded (a scan has none; it's the burst gap).
      if (e.key.length === 1 && e.key !== " ") {
        // Keep a mid-burst scan char out of the browser (see inBurst above);
        // the first char of a burst + ordinary typing are not pre-empted.
        if (inBurst && !isTypingInForm) e.preventDefault();
        buffer += e.key;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onScan, enabled]);
}
